// src/components/PostModal.tsx
// Share modal: client validation + direct Firestore create under firestore.rules.
// (No Cloud Functions — free Spark plan / hosting-only.)

import { useState, useRef, useEffect } from 'react';
import { X, Link as LinkIcon, AlertCircle, Loader2, CheckCircle } from 'lucide-react';
import { FaYoutube, FaInstagram, FaTwitter, FaSoundcloud } from 'react-icons/fa';
import {
  doc,
  setDoc,
  getDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db, handleFirestoreError, OperationType } from '../firebase';
import {
  parseSocialUrl,
  fetchYouTubeMetadata,
  fetchTwitterMetadata,
  fetchInstagramMetadata,
  fetchSoundCloudMetadata,
} from '../lib/socialUrlParser';
import { isAllowedShareUrl, collectionForPlatform } from '../lib/urlAllowlist';
import { checkShareRateLimit, recordShareAttempt } from '../lib/shareRateLimit';

interface PostModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface PostWriteData {
  url: string;
  originalUrl: string;
  platform: string;
  contentId: string;
  title: string;
  clicks: number;
  createdAt: ReturnType<typeof serverTimestamp>;
  authorUid: string;
  thumbnail?: string;
  author?: string;
  description?: string;
}

export default function PostModal({ isOpen, onClose }: PostModalProps) {
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');
  const [isValid, setIsValid] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [parsed, setParsed] = useState<ReturnType<typeof parseSocialUrl> | null>(null);
  // Honeypot — bots fill this; humans never see it
  const honeypotRef = useRef<HTMLInputElement>(null);
  const formOpenedAt = useRef(0);

  useEffect(() => {
    if (isOpen) {
      formOpenedAt.current = Date.now();
    }
  }, [isOpen]);

  const resetForm = () => {
    setUrl('');
    setError('');
    setIsValid(false);
    setParsed(null);
    formOpenedAt.current = Date.now();
    if (honeypotRef.current) honeypotRef.current.value = '';
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleUrlChange = (value: string) => {
    const trimmed = value.trim();
    setUrl(trimmed);
    setError('');

    if (!trimmed) {
      setIsValid(false);
      setParsed(null);
      return;
    }

    const result = parseSocialUrl(trimmed);
    setParsed(result);

    if (result.platform === 'other') {
      setIsValid(false);
      setError('Only YouTube, Instagram, X/Twitter and SoundCloud links allowed.');
    } else if (!isAllowedShareUrl(result.canonicalUrl || trimmed, result.platform)) {
      setIsValid(false);
      setError('That link host is not allowed.');
    } else {
      setIsValid(true);
    }
  };

  const ensureFullUrl = (input: string) =>
    input.match(/^https?:\/\//i) ? input : `https://${input}`;

  async function createPost(
    parsedResult: NonNullable<typeof parsed>,
    shareUrl: string
  ): Promise<boolean> {
    const collectionName = collectionForPlatform(parsedResult.platform);
    if (!collectionName) {
      setError('Unsupported platform.');
      return false;
    }

    const docRef = doc(db, collectionName, parsedResult.id);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      setError('This content has already been shared.');
      return false;
    }

    let title = 'Shared Content';
    let description = '';
    let thumbnail = '';
    let author = '';

    const metaUrl = parsedResult.canonicalUrl || shareUrl;
    if (!isAllowedShareUrl(metaUrl, parsedResult.platform)) {
      setError('That link host is not allowed.');
      return false;
    }

    if (parsedResult.platform === 'youtube') {
      const meta = await fetchYouTubeMetadata(metaUrl);
      if (meta.title) title = meta.title;
      if (meta.description) description = meta.description;
      if (meta.thumbnail) thumbnail = meta.thumbnail;
      if (meta.author) author = meta.author;
    } else if (parsedResult.platform === 'instagram') {
      const meta = await fetchInstagramMetadata(metaUrl);
      if (meta.title) title = meta.title;
      if (meta.description) description = meta.description;
      if (meta.thumbnail) thumbnail = meta.thumbnail;
      if (meta.author) author = meta.author;
    } else if (parsedResult.platform === 'twitter') {
      const meta = await fetchTwitterMetadata(metaUrl);
      if (meta.title) title = meta.title;
      if (meta.description) description = meta.description;
      if (meta.thumbnail) thumbnail = meta.thumbnail;
      if (meta.author) author = meta.author;
    } else if (parsedResult.platform === 'soundcloud') {
      const meta = await fetchSoundCloudMetadata(metaUrl);
      if (meta.title) title = meta.title;
      if (meta.description) description = meta.description;
      if (meta.thumbnail) thumbnail = meta.thumbnail;
      if (meta.author) author = meta.author;
    }

    const normalizedOriginal = ensureFullUrl(shareUrl);
    let finalTitle = title;
    if (finalTitle.length > 199) {
      finalTitle = finalTitle.substring(0, 196) + '...';
    }
    let finalDescription = description || '';
    if (finalDescription.length > 2000) {
      finalDescription = finalDescription.substring(0, 1997) + '...';
    }

    const postData: PostWriteData = {
      url: parsedResult.canonicalUrl || shareUrl,
      originalUrl: normalizedOriginal,
      platform: parsedResult.platform,
      contentId: parsedResult.id,
      title: finalTitle,
      clicks: 0,
      createdAt: serverTimestamp(),
      authorUid: 'anonymous',
    };
    if (thumbnail) postData.thumbnail = thumbnail;
    if (author) postData.author = author;
    if (finalDescription) postData.description = finalDescription;

    await setDoc(docRef, postData);
    return true;
  }

  const handlePost = async () => {
    if (!isValid || !parsed || !url) return;

    // Honeypot / timing checks
    if (honeypotRef.current?.value) {
      setError('Unable to share right now.');
      return;
    }
    if (Date.now() - formOpenedAt.current < 1200) {
      setError('Please wait a second and try again.');
      return;
    }

    const rate = checkShareRateLimit();
    if (!rate.allowed) {
      setError(rate.reason);
      return;
    }

    if (!isAllowedShareUrl(parsed.canonicalUrl || url, parsed.platform)) {
      setError('That link host is not allowed.');
      return;
    }

    setIsUploading(true);
    setError('');

    try {
      const created = await createPost(parsed, url);
      if (created) {
        recordShareAttempt();
        resetForm();
        onClose();
      }
    } catch (err: unknown) {
      const collectionName = collectionForPlatform(parsed.platform) || 'posts';
      handleFirestoreError(err, OperationType.WRITE, `${collectionName}/${parsed.id}`);
    } finally {
      setIsUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-60 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleClose} />

      <div className="relative bg-white dark:bg-slate-800 rounded-t-3xl sm:rounded-2xl w-full max-w-lg shadow-2xl p-6 sm:p-8 animate-in slide-in-from-bottom duration-300 sm:animate-in sm:zoom-in-95">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
            <LinkIcon size={20} className="text-pink-500" />
            Share Video Link
          </h2>
          <button onClick={handleClose} className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-full">
            <X size={20} className="text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Platform Icons */}
        <div className="flex gap-6 justify-center mb-6 text-gray-400">
          <FaYoutube size={28} className={parsed?.platform === 'youtube' ? 'text-red-500 scale-110' : ''} />
          <FaInstagram size={28} className={parsed?.platform === 'instagram' ? 'text-pink-600 scale-110' : ''} />
          <FaTwitter size={28} className={parsed?.platform === 'twitter' ? 'text-blue-400 scale-110' : ''} />
          <FaSoundcloud size={28} className={parsed?.platform === 'soundcloud' ? 'text-[#ff5500] scale-110' : ''} />
        </div>

        {/* Honeypot (hidden from users, visible to naive bots) */}
        <input
          ref={honeypotRef}
          type="text"
          name="website"
          autoComplete="off"
          tabIndex={-1}
          aria-hidden="true"
          className="absolute opacity-0 pointer-events-none h-0 w-0 -z-10"
        />

        <input
          type="text"
          value={url}
          onChange={(e) => handleUrlChange(e.target.value)}
          placeholder="Paste https://..."
          disabled={isUploading}
          className={`w-full p-4 rounded-xl border bg-slate-50 dark:bg-slate-900 dark:text-white focus:outline-none focus:ring-2 transition-all ${
            error ? 'border-red-300 bg-red-50' : 'border-gray-200 dark:border-slate-700 focus:ring-pink-500/20 focus:border-pink-500'
          }`}
        />

        {/* LIVE DETECTION */}
        {parsed && parsed.platform !== 'other' && (
          <div className="mt-3 flex items-center gap-2 text-green-600 dark:text-green-400 text-sm font-medium">
            <CheckCircle size={18} />
            Detected as {parsed.platform.toUpperCase()}
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 text-red-500 text-sm mt-3">
            <AlertCircle size={16} />
            {error}
          </div>
        )}

        <div className="flex justify-end gap-3 mt-8">
          <button
            onClick={handleClose}
            disabled={isUploading}
            className="px-4 py-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg"
          >
            Cancel
          </button>

          <button
            onClick={handlePost}
            disabled={!isValid || isUploading}
            className={`px-6 py-2 font-medium rounded-full transition-all flex items-center gap-2 min-w-[140px] justify-center ${
              isValid && !isUploading
                ? 'bg-pink-600 text-white hover:bg-pink-700 shadow-lg shadow-pink-200'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed dark:bg-slate-700'
            }`}
          >
            {isUploading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Sharing...
              </>
            ) : 'Share Video'}
          </button>
        </div>
      </div>
    </div>
  );
}
